// web-site 入口：首次模型配置引导 → 工程列表 + 新建/删除/重命名 → 打开工程（跳 shell）。
//
// 额外支持 aiMuse 剧集入口（?nle=1&projectId=&chapterId=&name=&ratio=&embed=1）：
// 1. 读取 /api/config，若服务端配置了默认模型，则自动写入占位凭证，免去用户手填；
// 2. 按「aiMuse 项目 ID」生成稳定工程 ID，整部剧共享同一套时间线与素材库；
// 3. 自动跳转 shell，用户无需经过剪辑器首页。
import { getLlmSettings, saveLlmSettings } from './settings.js';
import { resetAgent } from './agent.js';
import * as platform from './home_platform.js';
import * as museApi from './muse_api.js';

const $ = id => document.getElementById(id);

// 服务端默认凭证占位符：真实的 DEEPSEEK_API_KEY 只在 serve.cjs 中使用，绝不下发浏览器。
const SERVER_KEY_SENTINEL = '__muse_server__';
const ENTRY_PARAMS = new URLSearchParams(location.search);
const IS_NLE_ENTRY = ENTRY_PARAMS.get('nle') === '1';
const IS_EMBED = ENTRY_PARAMS.get('embed') === '1';

function show(view) {
    $('settings-view').hidden = view !== 'settings';
    $('home-view').hidden = view !== 'home';
}

// ---------- 默认模型配置 ----------
let cachedServerConfig = null;
/** 读取服务端公开配置；失败时返回 null，前端回退到手动配置。 */
async function loadServerConfig() {
    if (cachedServerConfig) return cachedServerConfig;
    try {
        const response = await fetch('/api/config', { cache: 'no-store' });
        if (!response.ok) return null;
        cachedServerConfig = await response.json();
        return cachedServerConfig;
    } catch {
        return null;
    }
}

/** 从 hash 读取 aiMuse 登录票据；hash 不会发送到服务端，也不会出现在 Referer。 */
function readHashToken() {
    const hash = location.hash.replace(/^#/, '');
    if (!hash) return '';
    return new URLSearchParams(hash).get('token') || '';
}

/** 票据落库后清理地址栏，避免留在浏览器历史与截图中。 */
function clearHashToken() {
    if (!location.hash) return;
    history.replaceState(null, '', location.pathname + location.search);
}

/** 把比例字符串转换为剪辑画布尺寸，未知比例按竖屏短剧兜底。 */
function resolveCanvasSize(rawRatio) {
    const matched = String(rawRatio || '').trim().match(/^(\d+)\s*[:：/xX]\s*(\d+)$/);
    if (!matched) return [1080, 1920];
    const width = Number(matched[1]);
    const height = Number(matched[2]);
    if (!width || !height) return [1080, 1920];
    if (width === height) return [1080, 1080];
    return width < height ? [1080, 1920] : [1920, 1080];
}

// ---------- 首次配置引导 ----------
async function initSettings() {
    const existing = await getLlmSettings();
    const token = readHashToken();
    const serverConfig = await loadServerConfig();

    // 服务端配置了默认模型：自动写入占位凭证，用户无需填写任何 key。
    if (serverConfig?.useServerKey) {
        // 开启登录校验但当前没有票据（直接打开剪辑器、或登录已过期）时，回到配置页并给出指引。
        if (serverConfig.requireAuth && !token && !existing.accessToken) {
            $('settingsStatus').textContent = '请从 aiMuse 编辑页的「剪辑」按钮进入，或手动填写模型配置';
            show('settings');
            return false;
        }
        const next = {
            ...existing,
            apiKey: SERVER_KEY_SENTINEL,
            baseURL: String(serverConfig.baseURL || '').trim(),
            modelName: String(serverConfig.modelName || 'deepseek-flash').trim(),
            // 票据同时用于 /api/llm 校验和 aiMuse 素材/草稿接口，必须保留。
            accessToken: token || existing.accessToken || '',
        };
        const changed = ['apiKey', 'baseURL', 'modelName', 'accessToken'].some(key => existing[key] !== next[key]);
        if (changed) {
            await saveLlmSettings(next);
            resetAgent();
        }
        clearHashToken();
        return true;
    }

    if (existing.apiKey && existing.baseURL && existing.modelName) {
        // 手动配置模式：如果本次带了新票据，同步更新后再进入。
        if (token && token !== existing.accessToken) {
            await saveLlmSettings({ ...existing, accessToken: token });
            resetAgent();
        }
        clearHashToken();
        return true;
    }

    $('apiKey').value = existing.apiKey || '';
    $('baseURL').value = existing.baseURL || '';
    $('modelName').value = existing.modelName || '';
    clearHashToken();
    show('settings');
    return false;
}

$('saveSettings').addEventListener('click', async () => {
    const current = await getLlmSettings();
    const settings = {
        apiKey: $('apiKey').value.trim(),
        baseURL: $('baseURL').value.trim(),
        modelName: $('modelName').value.trim(),
        // 保留本次入口带来的登录票据，避免手动保存后丢掉身份。
        accessToken: current.accessToken || '',
    };
    if (!settings.apiKey || !settings.baseURL || !settings.modelName) {
        $('settingsStatus').textContent = '三项都必填';
        return;
    }
    await saveLlmSettings(settings);
    resetAgent();
    show('home');
    renderProjects();
});

$('editSettings').addEventListener('click', async () => {
    const s = await getLlmSettings();
    // 默认凭证模式下不把占位符展示给用户，清空后由用户决定是否改为手动配置。
    $('apiKey').value = s.apiKey === SERVER_KEY_SENTINEL ? '' : (s.apiKey || '');
    $('baseURL').value = s.baseURL || '';
    $('modelName').value = s.modelName || '';
    show('settings');
});

// ---------- 工程列表 + 新建/删除/重命名 ----------
function defaultName() {
    const d = new Date();
    const pad = n => String(n).padStart(2, '0');
    return `工程 ${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

async function renderProjects() {
    const list = $('projectList');
    const projects = await platform.listProjects();
    list.innerHTML = '';
    if (!projects.length) {
        list.innerHTML = '<div class="empty">还没有工程，点击上方预设新建一个</div>';
        return;
    }
    for (const project of projects) {
        const el = document.createElement('div');
        el.className = 'project';
        el.innerHTML = `<div class="info"><div class="name"></div><div class="meta"></div></div>
            <div class="actions"><button class="rename">重命名</button><button class="danger">删除</button></div>`;
        el.querySelector('.name').textContent = project.name;
        el.querySelector('.meta').textContent = `最后编辑 ${formatDate(project.updatedAt)}`;
        el.querySelector('.info').addEventListener('click', () => openProject(project));
        el.querySelector('.rename').addEventListener('click', event => { event.stopPropagation(); renameProject(project); });
        el.querySelector('.danger').addEventListener('click', event => { event.stopPropagation(); removeProject(project); });
        list.appendChild(el);
    }
}

async function openProject(project) {
    await platform.openProject(project);
}

async function renameProject(project) {
    const name = prompt('重命名工程', project.name);
    if (name === null) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    await platform.renameProject(project.id, trimmed);
    await renderProjects();
}

async function removeProject(project) {
    if (!confirm(`删除工程「${project.name}」？此操作不可恢复。`)) return;
    await platform.deleteProject(project.id);
    await renderProjects();
}

async function createProject(width, height) {
    const name = defaultName();
    const protocol = {
        id: platform.randomUUID(), duration: 5_000_000, fps: 30,
        canvas_config: { width, height, ratio: `${width}:${height}` },
        // 默认跟随时间轴视频自动适应画布，用户可在播放器控制栏手动固定比例。
        canvas_fit: 'auto',
        tracks: [], materials: {},
    };
    const project = await platform.createProject({ name, protocol });
    await platform.openProject(project);
}

/** 取出顶层协议与所有时间线快照（多时间线工程的素材分散在各时间线里）。 */
function collectTimelineProtocols(protocol) {
    const list = Array.isArray(protocol?.muse_timelines) ? protocol.muse_timelines : [];
    const protocols = [protocol, ...list.map(item => item?.protocol).filter(Boolean)];
    // 素材池兜底字段（SDK 导出可能丢弃未引用素材）。
    if (protocol?.muse_material_pool) protocols.push({ materials: protocol.muse_material_pool });
    return protocols;
}

/** 统计协议里所有时间线的素材数量，用于判断本地是否已经有剪辑内容。 */
function countProtocolMaterials(protocol) {
    return collectTimelineProtocols(protocol).reduce((total, item) => {
        const groups = item?.materials;
        if (!groups || typeof groups !== 'object') return total;
        return total + Object.values(groups).reduce((count, list) => (
            count + (Array.isArray(list) ? list.length : 0)
        ), 0);
    }, 0);
}

/** 把全部时间线的视频素材按路径去重合并，用于跨设备恢复 OPFS 文件。 */
function mergeDraftVideos(protocol) {
    const videos = [];
    const seen = new Set();
    for (const item of collectTimelineProtocols(protocol)) {
        for (const video of item?.materials?.videos || []) {
            const key = String(video?.path || video?.id || '');
            if (!key || seen.has(key)) continue;
            seen.add(key);
            videos.push(video);
        }
    }
    return { materials: { videos } };
}

/** 入口页轻量状态遮罩：恢复服务端草稿素材时避免白屏。 */
function showEntryStatus(text) {
    let element = document.getElementById('nle-entry-status');
    if (!element) {
        element = document.createElement('div');
        element.id = 'nle-entry-status';
        element.style.cssText = [
            'position:fixed', 'inset:0', 'z-index:9999', 'display:flex',
            'align-items:center', 'justify-content:center', 'text-align:center',
            'padding:24px', 'background:#f5f7fa', 'color:#2b3a4a',
            'font:14px -apple-system, "PingFang SC", "Segoe UI", sans-serif',
        ].join(';');
        document.body.appendChild(element);
    }
    element.textContent = text;
}

/**
 * 跨设备恢复草稿时，本地 OPFS 还没有视频文件。
 * 按草稿里的素材名称匹配当前章节素材，下载并写回原来的 OPFS 路径，
 * 这样时间轴里的 segment / material 引用不需要改写。
 */
async function ensureDraftMaterials(protocol) {
    // 多时间线：素材可能只存在于某条非当前时间线里，恢复时要覆盖全部时间线。
    const restoreProtocol = mergeDraftVideos(protocol);
    const videos = restoreProtocol.materials.videos;
    if (!videos.length) return;
    showEntryStatus('正在检查项目剪辑素材…');
    try {
        const result = await museApi.restoreMaterialFiles(restoreProtocol, (index, total, material) => {
            showEntryStatus(`正在恢复剪辑素材（${index}/${total}）：${material.name}…`);
        });
        if (result.restored > 0) {
            showEntryStatus(`已恢复 ${result.restored} 个剪辑素材，正在打开编辑器…`);
        }
        if (result.missing > 0) {
            console.warn(`有 ${result.missing} 个草稿素材未能恢复`, result);
        }
    } catch (error) {
        console.warn('恢复剪辑草稿素材失败', error);
    }
}

/**
 * aiMuse 剧集入口：创建/复用项目级工程后直接进入编辑器。
 * 同一 aiMuse 项目的所有章节共享同一条时间线；章节只决定素材库展示的本章素材。
 * 进入前会读取 aiMuse 服务端草稿：服务端更新或本地还没有素材时，用服务端协议覆盖本地。
 */
async function enterNleProject() {
    const projectId = String(ENTRY_PARAMS.get('projectId') || '').trim();
    const chapterId = String(ENTRY_PARAMS.get('chapterId') || '').trim();
    if (!projectId || !chapterId) {
        show('home');
        await renderProjects();
        return;
    }
    const name = String(ENTRY_PARAMS.get('projectName') || ENTRY_PARAMS.get('name') || '').trim()
        || `项目 ${projectId}`;
    const [width, height] = resolveCanvasSize(ENTRY_PARAMS.get('ratio'));
    // 共享时间线：工程 ID 只带 aiMuse 项目 ID，不再包含章节。
    const localProjectId = `muse-${projectId}`;

    const existingSettings = await getLlmSettings();
    // 草稿版本只看项目；切换章节不能把共享草稿当成「另一个工程」。
    const sameProject = String(existingSettings.museProjectId || '') === projectId;
    const sameChapter = sameProject
        && String(existingSettings.museChapterId || '') === chapterId;
    const apiBase = String(ENTRY_PARAMS.get('apiBase') || cachedServerConfig?.museApiBaseUrl || '')
        .trim()
        .replace(/\/+$/, '');
    const appVersion = String(ENTRY_PARAMS.get('appVersion') || '').trim();
    const variant = String(ENTRY_PARAMS.get('variant') || 'original') === 'enhance' ? 'enhance' : 'original';

    // 先落上下文，后续 NLE 编辑器/聊天页都能读取同一份 apiBase、token 与项目/章节。
    await museApi.updateContext({
        apiBase,
        appVersion,
        variant,
        museProjectId: projectId,
        museChapterId: chapterId,
        importedStoryboardIds: sameChapter ? existingSettings.importedStoryboardIds : [],
    });

    // 首次进入共享工程：把该项目下最近编辑的章节工程迁移为项目级工程，旧工程保留。
    // 迁移失败不阻断进入，用户仍可在新的共享工程里重新剪辑。
    let migrated = null;
    try {
        migrated = await platform.migrateLegacyChapterProject({
            projectId,
            targetId: localProjectId,
            name,
            status: showEntryStatus,
        });
    } catch (error) {
        console.warn('迁移旧章节剪辑工程失败', error);
    }
    let project = migrated || await platform.ensureProject({
        id: localProjectId,
        name,
        width,
        height,
    });

    let appliedDraftProtocol = null;
    try {
        const draft = await museApi.fetchDraft();
        if (draft?.exists && draft.protocol) {
            const remoteVersion = Number(draft.version) || 0;
            const localVersion = sameProject ? Number(existingSettings.museDraftVersion) || 0 : 0;
            const localMaterialCount = countProtocolMaterials(project.protocol);
            // 服务端版本更新，或本地还是空工程（首次进入 / 清过缓存）时，以服务端草稿为准。
            if (remoteVersion > localVersion || localMaterialCount === 0) {
                // 旧草稿仍指向按章节拆分的 OPFS 目录，应用前统一改写到项目级目录。
                const draftProtocol = platform.rewriteLegacyProjectPaths(draft.protocol, projectId, localProjectId);
                project = await platform.applyRemoteDraft(localProjectId, draftProtocol, draft.updatedAt);
                appliedDraftProtocol = draftProtocol;
            }
            await museApi.updateContext({ museDraftVersion: remoteVersion });
        } else {
            await museApi.updateContext({ museDraftVersion: 0 });
        }
    } catch (error) {
        // 草稿读取失败不阻断进入编辑器，本地仍可继续编辑并稍后重试同步。
        console.warn('读取服务端剪辑草稿失败', error);
    }

    // 新设备/新浏览器没有 OPFS 视频文件：按草稿素材名下载并写回原路径。
    if (appliedDraftProtocol) {
        await ensureDraftMaterials(appliedDraftProtocol);
    }

    await platform.openProject(project, { embed: IS_EMBED });
}

// 点击预设卡片直接创建（默认时间戳名字，之后可在列表重命名）
document.querySelectorAll('.preset').forEach(el => {
    el.addEventListener('click', () => createProject(Number(el.dataset.w), Number(el.dataset.h)));
});

// ---------- 启动 ----------
(async () => {
    try {
        const configured = await initSettings();
        if (!configured) return;
        if (IS_NLE_ENTRY) {
            await enterNleProject();
            return;
        }
        show('home');
        await renderProjects();
    } catch (error) {
        // 入口页不要让异常变成全白：把失败原因展示在配置区，便于用户/运维定位。
        $('settingsStatus').textContent = `启动失败：${error?.message || error}`;
        show('settings');
    }
})();
